import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContenido } from "@/context/ContenidoContext";
import { toast } from "sonner";
import { Plus, Trash2, Star, Save, X } from "lucide-react";

function ItemForm({ initial, seccion, onSave, onCancel }) {
    const [nombre, setNombre] = useState(initial?.nombre || "");
    const [descripcion, setDescripcion] = useState(initial?.descripcion || "");
    const [precio, setPrecio] = useState(initial?.precio || "");
    const [estrella, setEstrella] = useState(initial?.estrella || false);
    const [grupo, setGrupo] = useState(initial?.grupo || "");

    const submit = (e) => {
        e.preventDefault();
        if (!nombre.trim() || !precio.trim()) {
            toast.error("Nombre y precio son obligatorios");
            return;
        }
        onSave({
            nombre: nombre.trim(),
            descripcion: descripcion.trim(),
            precio: precio.trim(),
            estrella,
            grupo: seccion.tipo === "grupos" ? grupo.trim() || null : null,
        });
    };

    return (
        <form
            onSubmit={submit}
            className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start bg-carbon p-4 border border-dorado/30"
        >
            <div className={seccion.tipo === "grupos" ? "md:col-span-3" : "md:col-span-4"}>
                <input
                    placeholder="Nombre del plato"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="input-line"
                    data-testid="item-form-nombre"
                />
            </div>
            <div className={seccion.tipo === "grupos" ? "md:col-span-3" : "md:col-span-5"}>
                <input
                    placeholder="Descripción"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    className="input-line"
                />
            </div>
            {seccion.tipo === "grupos" && (
                <div className="md:col-span-2">
                    <input
                        placeholder="Grupo (ej. Riojas)"
                        value={grupo}
                        onChange={(e) => setGrupo(e.target.value)}
                        className="input-line"
                    />
                </div>
            )}
            <div className="md:col-span-1">
                <input
                    placeholder="Precio"
                    value={precio}
                    onChange={(e) => setPrecio(e.target.value)}
                    className="input-line"
                    data-testid="item-form-precio"
                />
            </div>
            <div className="md:col-span-1 flex items-center justify-center pt-2">
                <button
                    type="button"
                    onClick={() => setEstrella(!estrella)}
                    aria-label="Destacar"
                    className={estrella ? "text-dorado" : "text-piedra"}
                >
                    <Star size={18} fill={estrella ? "currentColor" : "none"} />
                </button>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 justify-end pt-1">
                <button
                    type="submit"
                    className="text-dorado hover:text-crema p-2 border border-dorado/40 hover:border-dorado"
                    aria-label="Guardar"
                    data-testid="item-form-save"
                >
                    <Save size={16} />
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-piedra hover:text-crema p-2 border border-piedra/30"
                    aria-label="Cancelar"
                >
                    <X size={16} />
                </button>
            </div>
        </form>
    );
}

function ItemRow({ item, seccion, onUpdate, onDelete }) {
    const [editing, setEditing] = useState(false);

    if (editing) {
        return (
            <ItemForm
                initial={item}
                seccion={seccion}
                onSave={async (data) => {
                    await onUpdate(item.id, data);
                    setEditing(false);
                }}
                onCancel={() => setEditing(false)}
            />
        );
    }

    return (
        <div
            className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center px-4 py-3 border-b border-dorado/10 hover:bg-carbon/40"
            data-testid="carta-item-row"
        >
            <div className={seccion.tipo === "grupos" ? "md:col-span-3" : "md:col-span-4"}>
                <p className="font-heading text-lg text-crema flex items-center gap-2">
                    {item.nombre}
                    {item.estrella && (
                        <Star size={12} className="text-dorado" fill="currentColor" strokeWidth={0} />
                    )}
                </p>
            </div>
            <div className={seccion.tipo === "grupos" ? "md:col-span-3" : "md:col-span-5"}>
                <p className="font-body text-xs text-piedra italic">{item.descripcion}</p>
            </div>
            {seccion.tipo === "grupos" && (
                <div className="md:col-span-2">
                    <p className="font-body text-xs text-dorado/80">{item.grupo || "—"}</p>
                </div>
            )}
            <div className="md:col-span-1 font-heading italic text-dorado">{item.precio}</div>
            <div className="md:col-span-1" />
            <div className="md:col-span-2 flex items-center justify-end gap-2">
                <button
                    onClick={() => setEditing(true)}
                    className="font-body text-xs uppercase tracking-[0.2em] text-piedra hover:text-dorado"
                    data-testid="carta-item-edit"
                >
                    Editar
                </button>
                <button
                    onClick={() => onDelete(item.id)}
                    className="text-piedra hover:text-red-400"
                    aria-label="Eliminar"
                    data-testid="carta-item-delete"
                >
                    <Trash2 size={14} />
                </button>
            </div>
        </div>
    );
}

export default function CrmCarta() {
    const { user } = useAuth();
    const { refresh: refreshGlobal } = useContenido();
    const [carta, setCarta] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(null); // seccion_id

    const load = () => {
        setLoading(true);
        api.get("/contenido/carta")
            .then((r) => setCarta(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => {
                setLoading(false);
                refreshGlobal();
            });
    };

    useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const createItem = async (seccion_id, data) => {
        try {
            await api.post("/contenido/items", { seccion_id, ...data });
            toast.success("Plato añadido");
            setAdding(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const updateItem = async (id, data) => {
        try {
            await api.patch(`/contenido/items/${id}`, data);
            toast.success("Plato actualizado");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const deleteItem = async (id) => {
        if (!window.confirm("¿Eliminar este plato?")) return;
        try {
            await api.delete(`/contenido/items/${id}`);
            toast.success("Eliminado");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const renderItems = (seccion) => {
        if (seccion.tipo === "grupos") {
            // flatten so all wine items are in one list (with grupo column)
            return (seccion.grupos || []).flatMap((g) => g.items);
        }
        return seccion.items || [];
    };

    return (
        <div className="space-y-12">
            <header>
                <p className="font-body uppercase tracking-[0.3em] text-xs text-dorado mb-3">
                    Carta · Editor de contenido
                </p>
                <h1 className="font-heading text-5xl text-crema font-light">
                    Edita la <em className="italic text-dorado">carta</em>
                </h1>
                <p className="font-body text-piedra mt-4">
                    Los cambios se publican en la web al instante. Marca con la estrella los
                    platos destacados (sugerencia del chef).
                </p>
            </header>

            {loading ? (
                <p className="text-piedra italic font-body">Cargando carta...</p>
            ) : (
                carta.map((sec) => (
                    <section key={sec.id} className="border border-dorado/20 bg-carbon-suave/40">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-dorado/15">
                            <div>
                                <p className="font-body uppercase tracking-[0.25em] text-[0.65rem] text-dorado">
                                    {sec.subtitulo}
                                </p>
                                <h2 className="font-heading text-2xl text-crema mt-1">
                                    {sec.titulo}
                                </h2>
                            </div>
                            <button
                                onClick={() => setAdding(sec.id)}
                                className="font-body text-xs uppercase tracking-[0.2em] text-dorado border border-dorado/40 px-4 py-2 hover:bg-dorado hover:text-carbon transition-colors flex items-center gap-2"
                                data-testid="carta-add-item"
                            >
                                <Plus size={14} /> Añadir plato
                            </button>
                        </div>

                        {adding === sec.id && (
                            <div className="px-4 py-3">
                                <ItemForm
                                    seccion={sec}
                                    onSave={(data) => createItem(sec.id, data)}
                                    onCancel={() => setAdding(null)}
                                />
                            </div>
                        )}

                        <div>
                            {renderItems(sec).length === 0 ? (
                                <p className="px-5 py-6 italic text-piedra font-body text-sm">
                                    Aún no hay platos. Pulsa "Añadir plato".
                                </p>
                            ) : (
                                renderItems(sec).map((it) => (
                                    <ItemRow
                                        key={it.id}
                                        item={it}
                                        seccion={sec}
                                        onUpdate={updateItem}
                                        onDelete={deleteItem}
                                    />
                                ))
                            )}
                        </div>
                    </section>
                ))
            )}
        </div>
    );
}
